// Offscreen graph rendering for XML reports. The Rust report engine emits a
// `report-render-graph` event for each TGraph directive (the C++ spawned a
// hidden graph dialog and captured its bitmap); this module renders the same
// chart the graph windows show — fetched through `get_time_graph_data`, drawn
// with uPlot on a detached container — composites a legend strip, and posts
// the PNG back through `report_graph_rendered`.

import uPlot from "uplot";
import {
  buildGraphOpen,
  toolSetDiagQueryToUi,
  type ToolSetDialog,
  type ToolSetDiagQuerySaved,
} from "./toolSetLoad";
import { buildTimeSeriesChart, type ChartBuildResult } from "../components/chartBuilders/buildTimeSeriesChart";
import { buildXYChart } from "../components/chartBuilders/buildXYChart";
import { readUiVar } from "../theme/uiTheme";
import { formatGraphDate, TIGER_TO_UNIX_OFFSET_SECS } from "./tigerTime";
import { buildDiagQueryParams } from "./diagQueryParamsBridge";
import { flattenDiagReport } from "./diagReportLines";
import { fetchTurbineList } from "../api/channelSelection";
import { getChannelInfo } from "../api/getChannelInfo";
import { getTigerResource } from "./tigerResource";
import type { DiagTree } from "../types/diag";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

// In the web build a report runs as a server job: render results are POSTed back
// over HTTP instead of through the Tauri `report_*_rendered` commands. The web
// driver (runWebReport) installs this poster for the duration of a report; while
// unset (desktop), reply()/replyDiag() use invoke().
type WebResultPoster = (body: Record<string, unknown>) => Promise<void>;
let webResultPoster: WebResultPoster | null = null;

// A web report's graph-data fetch must not wait on the data broker forever when
// it can't serve the window (e.g. an archived / CD-ROM-gated range). The
// webserver runs a SINGLE, serial headless render worker, so a fetch with no
// bound doesn't just fail one graph — it wedges the worker: it never POSTs a
// result, the engine's render waiter hits its 180s RENDER_TIMEOUT ("Timeout
// waiting for graph render"), and every later graph in this report (and every
// future report) stalls behind the stuck fetch. Bound it like submitDiagQuery so
// an unservable graph aborts, the engine keeps the placeholder, and the worker
// moves on. Generous for a valid large historical window; well under the 180s
// server backstop so the report continues at the abort, not the timeout.
const WEB_REPORT_GRAPH_TIMEOUT_MS = 120_000;

/** Fetch+parse a report graph-data route on the web with a hard timeout, so a
 *  non-responsive data broker can't hang the (single, serial) render worker. */
async function fetchGraphJson<T>(url: string, what: string): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), WEB_REPORT_GRAPH_TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`${what} failed: ${res.status}`);
    return (await res.json()) as T;
  } catch (e) {
    if (controller.signal.aborted) {
      throw new Error(`${what} timed out (broker returned no data)`);
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

// A desktop `invoke` graph fetch has no built-in timeout, so a request the data
// server never answers (e.g. a HIST/compare fetch whose serving process died)
// would block until the report engine's 180s render-waiter backstop — and with
// several graphs that reads as a report that "never finishes". Bound it like the
// web path's `fetchGraphJson` so a stalled graph aborts, its slot renders the
// #### placeholder, and the report proceeds. Aborting the promise doesn't cancel
// the still-running backend fetch, but it lets the renderer reply to the engine
// and move on. Matches WEB_REPORT_GRAPH_TIMEOUT_MS; below the 180s backstop.
async function withInvokeTimeout<T>(p: Promise<T>, what: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const onTimeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${what} timed out (data server returned no data)`)),
      WEB_REPORT_GRAPH_TIMEOUT_MS,
    );
  });
  try {
    return await Promise.race([p, onTimeout]);
  } finally {
    clearTimeout(timer);
  }
}

// Data fetches used by the renderer, dual-pathed so the same render code runs in
// the desktop app (invoke) and the browser (fetch against the webserver routes).
async function fetchTimeGraphData(paramsStr: string): Promise<GraphData> {
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    return withInvokeTimeout(
      invoke<GraphData>("get_time_graph_data", { paramsStr }),
      "get_time_graph_data",
    );
  }
  return fetchGraphJson<GraphData>(
    `/api/get_time_graph_data?params_str=${encodeURIComponent(paramsStr)}`,
    "get_time_graph_data",
  );
}

async function fetchXyGraphData(paramsStr: string): Promise<XYGraphData> {
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    return withInvokeTimeout(
      invoke<XYGraphData>("get_xy_graph_data", { paramsStr }),
      "get_xy_graph_data",
    );
  }
  return fetchGraphJson<XYGraphData>(
    `/api/get_xy_graph_data?params_str=${encodeURIComponent(paramsStr)}`,
    "get_xy_graph_data",
  );
}

async function fetchSiteConfigName(): Promise<string> {
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    return invoke<string>("get_site_config_name").catch(() => "");
  }
  return fetch("/api/get_site_config_name")
    .then((r) => (r.ok ? r.text() : ""))
    .catch(() => "");
}

// A web report must not wait out the backend's 300s diag timeout when the broker
// can't serve the window. Bound the wait so the report falls back to the ####
// placeholder and finishes promptly; still generous enough for a valid historical
// query to load. (Aborting the fetch makes axum drop the handler, which clears
// the server-side active-query state.) This submitDiagQuery is used only by report
// rendering, so the Diagnostics window's own queries are unaffected.
const WEB_REPORT_DIAG_TIMEOUT_MS = 45_000;

async function submitDiagQuery(params: unknown): Promise<DiagTree> {
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    return invoke<DiagTree>("submit_diag_query", { params });
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), WEB_REPORT_DIAG_TIMEOUT_MS);
  try {
    const res = await fetch("/api/submit_diag_query", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(params),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`submit_diag_query failed: ${res.status}`);
    return (await res.json()) as DiagTree;
  } catch (e) {
    if (controller.signal.aborted) {
      throw new Error("diagnostics query timed out (broker returned no data)");
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

/** Report graphs take every plot colour from the template's TGraph config —
 *  the C++ XML plot loader (tiger_plot::load_plot_from_xml) always uses the
 *  colour prop, defaulting to black when absent. The interactive windows'
 *  palette fallback must not apply here. */
const REPORT_DEFAULT_PLOT_COLOUR = "#000000";

interface RenderRequest {
  request_id: number;
  width: number;
  height: number;
  graph_id: string;
  dialog: Extract<ToolSetDialog, { kind: "graph" }>;
}

// The fetch's series rows (Rust GraphSeries). `turbine` is the config the
// series was fetched from — needed to tell apart plots that share a tag
// across turbines (absent from older/XY responses).
interface GraphSeriesMeta {
  label: string;
  color: string;
  turbine?: string;
}

interface GraphData {
  series: GraphSeriesMeta[];
  data: number[][];
}

/** get_xy_graph_data response: per-plot X/Y sample arrays (index-aligned with
 *  the y-paired plots buildGraphOpen sends). The backend leaves each series
 *  colour empty for XY, so colour comes from the template plots. */
interface XYGraphData {
  series: GraphSeriesMeta[];
  x_values: number[][];
  y_values: number[][];
}

const LEGEND_ROW_HEIGHT = 18;
const LEGEND_HEADER_HEIGHT = 16;
const LEGEND_PAD = 8;

/** The legend must never take over the template-defined graph height: cap it
 *  to this fraction of the requested PNG height and truncate the rows. */
const MAX_LEGEND_FRACTION = 0.6;

/** Legend sizing for `seriesCount` rows within a `reqHeight`-px PNG. When the
 *  full legend would exceed `MAX_LEGEND_FRACTION` of the height, it is capped
 *  and the last slot is reserved for a "… +N more" line so the reader can tell
 *  rows were dropped (trend graphs triple their rows via (AVG)/(MAX)/(MIN), so
 *  an uncapped legend can otherwise swallow the whole plot). */
export function legendLayout(
  seriesCount: number,
  reqHeight: number,
): { legendHeight: number; visibleRows: number; hiddenCount: number } {
  const heightFor = (rows: number) =>
    LEGEND_PAD * 2 + LEGEND_HEADER_HEIGHT + rows * LEGEND_ROW_HEIGHT;
  // Always allow at least the header plus one row, even at tiny heights.
  const cap = Math.max(heightFor(1), Math.floor(reqHeight * MAX_LEGEND_FRACTION));
  if (heightFor(seriesCount) <= cap) {
    return { legendHeight: heightFor(seriesCount), visibleRows: seriesCount, hiddenCount: 0 };
  }
  const slots = Math.max(
    1,
    Math.floor((cap - LEGEND_PAD * 2 - LEGEND_HEADER_HEIGHT) / LEGEND_ROW_HEIGHT),
  );
  const visibleRows = slots - 1; // last slot shows the "+N more" line
  return {
    legendHeight: heightFor(slots),
    visibleRows,
    hiddenCount: seriesCount - visibleRows,
  };
}

/** One composed legend row, mirroring the C++ graph dialog's channel-info
 *  columns (tag / description / turbine / min / max). */
interface LegendRow {
  color: string;
  tag: string;
  description: string;
  turbine: string;
  min: string;
  max: string;
}

const INVALID_TIGER_VALUE = 3.4028234663852886e38;

/** Report graphs spanning more than this show date-only x-axis labels. */
const DATE_ONLY_SPAN_SECS = 3 * 24 * 3600;

/** True when the fetched series cover more than `DATE_ONLY_SPAN_SECS`
 *  (timestamps come back ascending, so first/last bound each series). */
export function reportSpanExceedsThreeDays(data: number[][], seriesCount: number): boolean {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < seriesCount; i++) {
    const ts = data[i * 2] ?? [];
    if (ts.length === 0) continue;
    if (ts[0] < min) min = ts[0];
    if (ts[ts.length - 1] > max) max = ts[ts.length - 1];
  }
  return max - min > DATE_ONLY_SPAN_SECS;
}

function seriesRange(vals: number[] | undefined): { min: string; max: string } {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const v of vals ?? []) {
    if (!Number.isFinite(v) || Math.abs(v) >= INVALID_TIGER_VALUE) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (min > max) return { min: "----", max: "----" };
  return { min: min.toFixed(2), max: max.toFixed(2) };
}

/** Truncate `text` with a trailing ellipsis so its rendered width fits within
 *  `maxWidth` px, measured with the context's current font. Keeps each legend
 *  column inside its own width instead of overrunning the next one. */
export function fitText(
  g: { measureText(text: string): { width: number } },
  text: string,
  maxWidth: number,
): string {
  if (maxWidth <= 0) return "";
  if (g.measureText(text).width <= maxWidth) return text;
  const ELLIPSIS = "…";
  // Binary-search the longest prefix whose `prefix + …` still fits.
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (g.measureText(text.slice(0, mid) + ELLIPSIS).width <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return lo > 0 ? text.slice(0, lo) + ELLIPSIS : ELLIPSIS;
}

/** Legend rows for the fetched series. The fetch skips plots whose tag can't
 *  be resolved, so the series can be fewer than the dialog's plots — match
 *  each series back to its plot by turbine + tag (multi-turbine graphs chart
 *  the same tag on every plot, so the tag alone is ambiguous), consuming each
 *  plot at most once, falling back to tag-only then position. */
export function buildLegendRows(
  series: GraphSeriesMeta[],
  plots: { turbine: string; channel_name: string }[],
  channelInfos: { description?: string }[],
  turbineNames: Map<string, string>,
  data: number[][],
): LegendRow[] {
  const used = new Set<number>();
  return series.map((s, i) => {
    let plotIndex = plots.findIndex(
      (p, pi) =>
        !used.has(pi) &&
        p.channel_name === s.label &&
        (!s.turbine || p.turbine === s.turbine),
    );
    if (plotIndex < 0) plotIndex = plots.findIndex((p, pi) => !used.has(pi) && p.channel_name === s.label);
    if (plotIndex < 0) plotIndex = i;
    used.add(plotIndex);
    const plot = plots[plotIndex];
    const turbineCfg = s.turbine || plot?.turbine || "";
    return {
      color: s.color,
      tag: s.label,
      description: channelInfos[plotIndex]?.description ?? "",
      turbine: turbineCfg ? turbineNames.get(turbineCfg) ?? turbineCfg : "",
      ...seriesRange(data[i * 2 + 1]),
    };
  });
}

/** Legend rows for a graph whose fetch returned no series at all: one row per
 *  template plot, with no min/max — the rows the C++ graph dialog's
 *  channel-info list showed for its plots before any data arrived. */
function legendRowsFromPlots(
  plots: { turbine: string; channel_name: string; colour?: string | null }[],
  channelInfos: { description?: string }[],
  turbineNames: Map<string, string>,
): LegendRow[] {
  return plots.map((p, i) => ({
    color: p.colour || REPORT_DEFAULT_PLOT_COLOUR,
    tag: p.channel_name,
    description: channelInfos[i]?.description ?? "",
    turbine: p.turbine ? turbineNames.get(p.turbine) ?? p.turbine : "",
    ...seriesRange([]),
  }));
}

/** An empty graph: the axis frame and grid, no traces.
 *
 *  A TGraph directive replaces a picture the template already carries — the
 *  graph from whenever the template was last saved. Answering the engine with
 *  an error leaves that stale picture in the finished report (process_graph
 *  keeps the placeholder), so a window with no data publishes an old graph as
 *  if it were this week's. The C++ could not do that: it captured the hidden
 *  graph dialog's window whatever the window showed, and a dialog with no data
 *  is an empty graph. So an empty graph is what we draw.
 *
 *  `xRange` is the graph's own (Unix-seconds) window when it has one — a
 *  historical graph's saved range. Without one there is no honest scale to
 *  label, so the ticks are drawn bare; the y axis is always bare, since no
 *  data means no y scale either. */
function buildEmptyChart(
  width: number,
  height: number,
  xRange: [number, number] | null,
): ChartBuildResult {
  const axisStroke = () => readUiVar("plotAxis", "#333");
  const gridStroke = () => readUiVar("plotGrid", "#e0e0e0");
  const bare = (_u: uPlot, splits: number[]) => splits.map(() => "");
  return {
    opts: {
      width,
      height,
      series: [{}],
      scales: {
        // A time scale only when there is a window to label: uPlot then picks
        // date-aligned ticks (the same way a real report graph's do).
        x: { time: xRange != null, auto: false, range: xRange ?? [0, 1] },
        y: { auto: false, range: [0, 1] },
      },
      axes: [
        {
          stroke: axisStroke,
          grid: { stroke: gridStroke, width: 1 },
          space: 90,
          values: xRange
            ? (_u: uPlot, splits: number[]) =>
                splits.map((v) => formatGraphDate(new Date(v * 1000)))
            : bare,
        },
        { stroke: axisStroke, grid: { stroke: gridStroke, width: 1 }, values: bare },
      ],
      legend: { show: false },
      cursor: { drag: { x: false, y: false } },
      // Tiger timestamps are wall-clock encoded zone-neutrally and the labels
      // decode them with UTC getters, so the ticks must be generated on the
      // same basis (as buildTimeSeriesChart does).
      tzDate: (ts: number) => uPlot.tzDate(new Date(ts * 1000), "Etc/UTC"),
    },
    aligned: [[]],
  };
}

/** The graph's own window as Unix seconds, when it saved one (report graph
 *  ranges are Tiger seconds). Used only to label an empty graph's x axis. */
function emptyChartXRange(
  dialog: Extract<ToolSetDialog, { kind: "graph" }>,
): [number, number] | null {
  const start = dialog.hist_start_time;
  const end = dialog.hist_end_time;
  if (!start || !end || end <= start) return null;
  return [start - TIGER_TO_UNIX_OFFSET_SECS, end - TIGER_TO_UNIX_OFFSET_SECS];
}

async function reply(requestId: number, pngBase64?: string, error?: string): Promise<void> {
  if (webResultPoster) {
    await webResultPoster({
      kind: "graph",
      request_id: requestId,
      png_base64: pngBase64 ?? null,
      error: error ?? null,
    });
    return;
  }
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("report_graph_rendered", {
    requestId,
    pngBase64: pngBase64 ?? null,
    error: error ?? null,
  }).catch((e) => console.error("report_graph_rendered failed:", e));
}

/** Render the chart canvas + legend into a PNG. The legend mirrors the C++
 *  graph dialog's channel-info list control: a header row then one row per
 *  plot, columns swatch | Tag Name | Min | Max | Description | Turbine. */
function composePng(
  chartRoot: HTMLElement,
  rows: LegendRow[],
  width: number,
  height: number,
  legendHeight: number,
  hiddenCount = 0,
): string | null {
  const chartCanvas = chartRoot.querySelector("canvas");
  if (!chartCanvas) return null;

  const out = document.createElement("canvas");
  out.width = width;
  out.height = height;
  const g = out.getContext("2d");
  if (!g) return null;

  g.fillStyle = "#ffffff";
  g.fillRect(0, 0, width, height);
  // The uPlot canvas is device-pixel scaled; drawImage rescales to CSS size.
  g.drawImage(chartCanvas, 0, 0, width, height - legendHeight);

  // Column boundaries proportioned like the original capture.
  const colTag = Math.max(30, Math.floor(width * 0.04));
  const colMin = Math.floor(width * 0.18);
  const colMax = Math.floor(width * 0.255);
  const colDescr = Math.floor(width * 0.35);
  const colTurbine = Math.floor(width * 0.77);

  const legendTop = height - legendHeight;
  g.textBaseline = "middle";

  let y = legendTop + LEGEND_PAD + LEGEND_HEADER_HEIGHT / 2;
  g.font = "bold 12px sans-serif";
  g.fillStyle = "#111111";
  g.fillText(getTigerResource("TIGER_GRAPH_TAGNAME"), colTag, y);
  g.fillText(getTigerResource("TIGER_GRAPH_MIN"), colMin, y);
  g.fillText(getTigerResource("TIGER_GRAPH_MAX"), colMax, y);
  g.fillText(getTigerResource("TIGER_GRAPH_DESCR"), colDescr, y);
  g.fillText(getTigerResource("TIGER_GRAPH_TURBINE"), colTurbine, y);
  y += LEGEND_HEADER_HEIGHT;

  // Truncate each cell to the gap before the next column so long tags /
  // descriptions clip with an ellipsis instead of overrunning their neighbour.
  const COL_GAP = 6;
  g.font = "12px sans-serif";
  for (const row of rows) {
    g.fillStyle = row.color;
    g.fillRect(8, y - 5, 16, 10);
    g.fillStyle = "#111111";
    g.fillText(fitText(g, row.tag, colMin - colTag - COL_GAP), colTag, y);
    g.fillText(fitText(g, row.min, colMax - colMin - COL_GAP), colMin, y);
    g.fillText(fitText(g, row.max, colDescr - colMax - COL_GAP), colMax, y);
    g.fillText(fitText(g, row.description, colTurbine - colDescr - COL_GAP), colDescr, y);
    g.fillText(fitText(g, row.turbine, width - colTurbine - LEGEND_PAD), colTurbine, y);
    y += LEGEND_ROW_HEIGHT;
  }

  // Truncation indicator: the legend was capped to keep the plot readable
  // within the template-defined height (legendLayout).
  if (hiddenCount > 0) {
    g.font = "italic 12px sans-serif";
    g.fillStyle = "#555555";
    g.fillText(
      fitText(g, `… ${hiddenCount} more channel${hiddenCount === 1 ? "" : "s"}`, width - colTag - LEGEND_PAD),
      colTag,
      y,
    );
  }

  const url = out.toDataURL("image/png");
  const comma = url.indexOf(",");
  return comma > 0 ? url.slice(comma + 1) : null;
}

async function renderRequest(req: RenderRequest): Promise<void> {
  // Template colours rule; plots without one are black (the C++ XML loader's
  // default), never the interactive windows' palette.
  const dialog = {
    ...req.dialog,
    plots: req.dialog.plots.map((p) => ({ ...p, colour: p.colour || REPORT_DEFAULT_PLOT_COLOUR })),
  };
  const open = buildGraphOpen(dialog);
  if (!open) {
    await reply(req.request_id, undefined, `graph type ${req.dialog.graph_type} has no plots or no Tauri equivalent`);
    return;
  }
  // XY-family graphs use a different fetch + chart builder (points, not a time
  // axis); the time-series modes share the path below.
  if (open.mode === "xy" || open.mode === "xyTrend" || open.mode === "xyTrendMulti") {
    await renderXYGraph(req, dialog, open.mode, open.params);
    return;
  }

  const gd = await fetchTimeGraphData(open.params);

  // Legend metadata: channel descriptions + turbine display names.
  // channelInfos is plot-ordered; buildLegendRows re-matches series to plots
  // by tag name since unresolvable plots are skipped by the fetch.
  const [channelInfos, turbines] = await Promise.all([
    getChannelInfo(
      dialog.plots.map((p) => ({ config_name: p.turbine, channel_name: p.channel_name })),
      open.mode === "trend",
    ).catch(() => []),
    fetchTurbineList().catch(() => []),
  ]);
  const turbineNames = new Map(turbines.map((t) => [t.config_name, t.turbine_name]));

  // No series at all (every plot's channel failed to resolve): the graph is
  // empty, so render it empty — listing the template's plots, as the C++
  // dialog's channel-info list did.
  if (!gd || gd.series.length === 0) {
    const rows = legendRowsFromPlots(dialog.plots, channelInfos, turbineNames);
    await paintEmptyChart(req, dialog, rows);
    return;
  }

  const layout = legendLayout(gd.series.length, req.height);
  const legendRows = buildLegendRows(gd.series, dialog.plots, channelInfos, turbineNames, gd.data)
    .slice(0, layout.visibleRows);
  const chartHeight = Math.max(100, req.height - layout.legendHeight);

  const built = buildTimeSeriesChart({
    width: req.width,
    height: chartHeight,
    series: gd.series,
    data: gd.series.map((_s, i) => ({ ts: gd.data[i * 2] ?? [], vals: gd.data[i * 2 + 1] ?? [] })),
    visibility: gd.series.map(() => true),
    yScaleMode: "auto",
    // A multitime graph overlays runs from different dates on one elapsed
    // axis: the backend already rebases each plot's timestamps to its own
    // event start (TimeOriginMode::EventStart), so the ticks must read as
    // durations, not as dates near the Tiger epoch.
    xAxisMode: open.mode === "multiTime" ? "elapsed" : "absolute",
    // Long-range report graphs label ticks with dates only.
    xDateOnly:
      open.mode !== "multiTime" && reportSpanExceedsThreeDays(gd.data, gd.series.length),
    // Event graphs render as one continuous line — no gap breaks.
    spanGaps: open.mode === "event",
  });
  // Series resolved but none carried a sample (the usual "no data for this
  // window"): still an empty graph, not a reason to keep the old picture.
  if (!built) {
    await paintEmptyChart(req, dialog, legendRows);
    return;
  }

  await paintChartToPng(req, built, legendRows, layout, chartHeight);
}

/** Render + post the empty graph for `req`, with `rows` as its channel-info
 *  legend. Shared by every "nothing to plot" path (time-series and XY). */
async function paintEmptyChart(
  req: RenderRequest,
  dialog: Extract<ToolSetDialog, { kind: "graph" }>,
  rows: LegendRow[],
): Promise<void> {
  // Capped like a populated graph's legend, so a template listing many plots
  // cannot swallow the (empty) chart either.
  const layout = legendLayout(rows.length, req.height);
  const chartHeight = Math.max(100, req.height - layout.legendHeight);
  const built = buildEmptyChart(req.width, chartHeight, emptyChartXRange(dialog));
  await paintChartToPng(req, built, rows.slice(0, layout.visibleRows), layout, chartHeight);
}

/** Draw a built uPlot chart on a detached container, composite the legend, and
 *  post the PNG back. Shared by the time-series and XY report paths. */
async function paintChartToPng(
  req: RenderRequest,
  built: ChartBuildResult,
  legendRows: LegendRow[],
  layout: { legendHeight: number; hiddenCount: number },
  chartHeight: number,
): Promise<void> {
  // uPlot needs a live container; keep it out of view while it draws.
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-100000px";
  container.style.top = "0";
  container.style.width = `${req.width}px`;
  container.style.height = `${chartHeight}px`;
  document.body.appendChild(container);

  let plot: uPlot | null = null;
  try {
    plot = new uPlot(built.opts, built.aligned, container);
    // uPlot defers its draw to a microtask (commit() → microTask(_commit));
    // capturing synchronously yields a blank canvas. Yield a macrotask (which
    // flushes pending microtasks first) plus a frame before reading pixels.
    await new Promise((resolve) => setTimeout(resolve, 0));
    // The headless render worker runs as a HIDDEN browser page, and browsers
    // pause/throttle requestAnimationFrame in hidden tabs — there the rAF
    // callback may never fire, hanging the render until the engine's 180s
    // timeout ("Timeout waiting for graph render"). Race rAF against a short
    // timer so we still wait a real frame when one comes, but never block on it.
    await new Promise<void>((resolve) => {
      let settled = false;
      const done = () => { if (!settled) { settled = true; resolve(); } };
      requestAnimationFrame(done);
      setTimeout(done, 50);
    });
    const png = composePng(
      container, legendRows, req.width, req.height, layout.legendHeight, layout.hiddenCount,
    );
    if (png) {
      await reply(req.request_id, png);
    } else {
      await reply(req.request_id, undefined, "failed to capture report graph canvas");
    }
  } finally {
    plot?.destroy();
    container.remove();
  }
}

/** XY-family report graphs (XY / Trend-XY / Multi-Trend-XY). Mirrors the
 *  interactive XY window: fetch paired X/Y samples via `get_xy_graph_data` and
 *  draw points with `buildXYChart`. The response arrays are index-aligned with
 *  the y-paired plots `buildGraphOpen` sends, and the backend leaves the series
 *  colour empty for XY, so colour + turbine come from the template plots. The
 *  legend's tag is the Y channel (the fetch labels each series with it) and
 *  min/max bound the Y values. */
async function renderXYGraph(
  req: RenderRequest,
  dialog: Extract<ToolSetDialog, { kind: "graph" }>,
  mode: "xy" | "xyTrend" | "xyTrendMulti",
  params: string,
): Promise<void> {
  const gd = await fetchXyGraphData(params);

  // get_xy_graph_data only includes plots that carry a Y channel, in order.
  const pairs = dialog.plots.filter((p) => p.y_channel_name);
  const isTrend = mode === "xyTrend" || mode === "xyTrendMulti";
  const plotColour = (i: number) => pairs[i]?.colour || REPORT_DEFAULT_PLOT_COLOUR;

  const [channelInfos, turbines] = await Promise.all([
    getChannelInfo(
      pairs.map((p) => ({ config_name: p.y_turbine || p.turbine, channel_name: p.y_channel_name ?? "" })),
      isTrend,
    ).catch(() => []),
    fetchTurbineList().catch(() => []),
  ]);
  const turbineNames = new Map(turbines.map((t) => [t.config_name, t.turbine_name]));

  // No series at all: an empty graph listing the template's y-paired plots.
  if (!gd || gd.series.length === 0) {
    const rows = legendRowsFromPlots(
      pairs.map((p) => ({
        turbine: p.y_turbine || p.turbine,
        channel_name: p.y_channel_name ?? "",
        colour: p.colour,
      })),
      channelInfos,
      turbineNames,
    );
    await paintEmptyChart(req, dialog, rows);
    return;
  }

  const layout = legendLayout(gd.series.length, req.height);
  const legendRows: LegendRow[] = gd.series.slice(0, layout.visibleRows).map((s, i) => {
    const plot = pairs[i];
    const turbineCfg = plot ? plot.y_turbine || plot.turbine : "";
    return {
      color: plotColour(i),
      tag: s.label,
      description: channelInfos[i]?.description ?? "",
      turbine: turbineCfg ? turbineNames.get(turbineCfg) ?? turbineCfg : "",
      ...seriesRange(gd.y_values[i]),
    };
  });

  const chartHeight = Math.max(100, req.height - layout.legendHeight);

  const built = buildXYChart({
    width: req.width,
    height: chartHeight,
    series: gd.series.map((s, i) => ({ label: s.label, color: plotColour(i) })),
    data: gd.series.map((_s, i) => ({ x: gd.x_values[i] ?? [], y: gd.y_values[i] ?? [] })),
    visibility: gd.series.map(() => true),
    yScaleMode: "auto",
    xScaleMode: "auto",
  });
  // Series resolved but no plottable pairs — an empty graph, not a reason to
  // keep the template's old picture.
  if (!built) {
    await paintEmptyChart(req, dialog, legendRows);
    return;
  }

  await paintChartToPng(req, built, legendRows, layout, chartHeight);
}

// ── TDiagnostics: run the query and flatten the displayed lines ─────────────

interface DiagRenderRequest {
  request_id: number;
  query: ToolSetDiagQuerySaved;
  /** Levels of children to expand below the roots (the directive's
   *  expand_level). u32::MAX from the backend means fully expanded. */
  expand_level: number;
}

async function replyDiag(
  requestId: number,
  lines?: { indent: number; text: string; color: string }[],
  error?: string,
): Promise<void> {
  if (webResultPoster) {
    await webResultPoster({
      kind: "diagnostics",
      request_id: requestId,
      lines: lines ?? null,
      error: error ?? null,
    });
    return;
  }
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("report_diag_rendered", {
    requestId,
    lines: lines ?? null,
    error: error ?? null,
  }).catch((e) => console.error("report_diag_rendered failed:", e));
}

/** Same code path as the diagnostics window: tool-set query → UI params →
 *  submit_diag_query → flattenDiagReport (the single source of display truth). */
async function renderDiagRequest(req: DiagRenderRequest): Promise<void> {
  const ui = toolSetDiagQueryToUi(req.query);
  const turbines = await fetchTurbineList();
  const siteConfigName = await fetchSiteConfigName();

  const params = buildDiagQueryParams(
    ui,
    turbines.map((t) => t.config_name),
    siteConfigName || undefined,
  );
  const tree = await submitDiagQuery(params);

  const lines = flattenDiagReport({
    tree,
    queryType: ui.queryType,
    // Per-row turbine prefix only for whole-site queries on multi-turbine sites.
    showTurbineName: !req.query.turbine && turbines.length > 1,
    turbineShortNames: new Map(turbines.map((t) => [t.config_name, t.short_name])),
    // u32::MAX (fully expanded) overflows JS bit ops but compares fine as a float.
    expandLevel: req.expand_level,
  });
  await replyDiag(req.request_id, lines);
}

/** Start listening for report render requests (graphs + diagnostics). Call
 *  once from the main window (Tauri runtime only). */
export async function initReportGraphRenderer(): Promise<void> {
  if (typeof window === "undefined" || !("__TAURI_INTERNALS__" in window)) return;
  const { listen } = await import("@tauri-apps/api/event");
  await listen<RenderRequest>("report-render-graph", (event) => {
    renderRequest(event.payload).catch(async (e) => {
      console.error("report graph render failed:", e);
      await reply(event.payload.request_id, undefined, String(e));
    });
  });
  await listen<DiagRenderRequest>("report-render-diagnostics", (event) => {
    renderDiagRequest(event.payload).catch(async (e) => {
      console.error("report diagnostics render failed:", e);
      await replyDiag(event.payload.request_id, undefined, String(e));
    });
  });
}

// The webserver's render worker is a SINGLE, serial headless browser: if one
// render hangs (a paused-rAF frame wait, or an unbounded data/channel/turbine
// fetch the broker never answers), it must not wedge the worker or leave the
// engine's render waiter sitting until its 180s RENDER_TIMEOUT. Bound every
// render so the worker always replies (error on timeout) and moves on. Above the
// graph-data fetch bound (which fires first, cancels the fetch, and gives a more
// specific error) and below the 180s server backstop.
const WORKER_RENDER_TIMEOUT_MS = 150_000;

/** Run one render to completion or its timeout, always resolving (and replying
 *  to the engine) so the single serial worker can advance to the next request. */
async function renderWithBound(
  requestId: number,
  kind: "graph" | "diagnostics",
  render: Promise<void>,
): Promise<void> {
  const replyError = (msg: string) =>
    kind === "graph" ? reply(requestId, undefined, msg) : replyDiag(requestId, undefined, msg);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const onTimeout = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      void replyError(`render timed out after ${WORKER_RENDER_TIMEOUT_MS}ms`).finally(() => resolve());
    }, WORKER_RENDER_TIMEOUT_MS);
  });
  try {
    await Promise.race([render.catch((e) => replyError(String(e))), onTimeout]);
  } finally {
    clearTimeout(timer);
  }
}

/** Drive one report job to completion: poll `/next`, render each request with
 *  the same code the desktop uses, POST the result. Used by the headless render
 *  worker — graphs/diagnostics no longer render in the requesting user's tab. */
export async function driveReportJob(jobPath: string): Promise<void> {
  webResultPoster = async (body) => {
    await fetch(`${jobPath}/result`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  };
  try {
    for (;;) {
      const nextRes = await fetch(`${jobPath}/next`);
      if (!nextRes.ok) {
        throw new Error((await nextRes.text()) || `report poll failed: ${nextRes.status}`);
      }
      const next = (await nextRes.json()) as
        | { kind: "graph"; request: RenderRequest }
        | { kind: "diagnostics"; request: DiagRenderRequest }
        | { kind: "complete" }
        | { kind: "error"; message: string };
      if (next.kind === "complete") break;
      if (next.kind === "error") throw new Error(next.message);
      if (next.kind === "graph") {
        await renderWithBound(next.request.request_id, "graph", renderRequest(next.request));
      } else {
        await renderWithBound(next.request.request_id, "diagnostics", renderDiagRequest(next.request));
      }
    }
  } finally {
    webResultPoster = null;
  }
}

/** Headless render worker. Run in the webserver's headless browser
 *  (`?render_worker=`): forever claim the next queued report job and drive it.
 *  An error on one job is logged; the worker continues to the next. */
export async function runRenderWorker(): Promise<void> {
  const pause = () => new Promise((r) => setTimeout(r, 2000));
  for (;;) {
    try {
      const res = await fetch("/api/render-worker/claim");
      if (!res.ok) {
        await pause();
        continue;
      }
      const { job_id } = (await res.json()) as { job_id: string | null };
      if (!job_id) continue; // claim long-polled and timed out; re-claim
      await driveReportJob(`/api/report/${encodeURIComponent(job_id)}`);
    } catch (e) {
      console.error("render worker error:", e);
      await pause();
    }
  }
}
